import { useCallback, useRef, useState, type DragEvent } from 'react';
import { Spinner } from 'react-bootstrap';
import { ArrowLeft, ArrowRight, Check, CloudUpload, Linkedin, Sparkles } from 'lucide-react';

type ResumeStageProps = {
  parsing: boolean;
  hasDraft: boolean;
  resumeName: string;
  resumeSizeLabel?: string;
  error?: string;
  onSelectFile: (file: File) => void;
  onBack: () => void;
  onContinue: () => void;
};

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatResumeSize(bytes?: number | null): string {
  if (bytes == null) return '';
  return formatBytes(bytes);
}

export default function ResumeStage({
  parsing,
  hasDraft,
  resumeName,
  resumeSizeLabel,
  error,
  onSelectFile,
  onBack,
  onContinue,
}: ResumeStageProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const takeFile = useCallback(
    (file: File | undefined | null) => {
      if (!file || parsing) return;
      onSelectFile(file);
    },
    [onSelectFile, parsing]
  );

  const openPicker = () => {
    if (parsing) return;
    inputRef.current?.click();
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
    takeFile(event.dataTransfer.files?.[0]);
  };

  const showSuccess = hasDraft && Boolean(resumeName) && !parsing;
  const sizeMeta = resumeSizeLabel ? `${resumeSizeLabel} • Uploaded just now` : 'Uploaded just now';

  return (
    <div className="ssr-resume">
      <div className="ssr-resume__inner">
        <header className="ssr-resume__header">
          <p className="ssr-resume__eyebrow">Step 2 of 4</p>
          <h1 className="ssr-heading ssr-resume__title">Upload your resume</h1>
          <p className="ssr-resume__lede">
            Upload your resume and we&apos;ll use AI to create your profile, highlight your
            experience, skills, and suggest screening prompts.
          </p>
        </header>

        {error ? <div className="ssr-resume__error">{error}</div> : null}

        <div
          className={`ssr-resume__dropzone${dragging ? ' is-dragging' : ''}${
            parsing ? ' is-parsing' : ''
          }`}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragging(false);
          }}
          onDrop={onDrop}
          onClick={openPicker}
        >
          {parsing ? (
            <div className="ssr-resume__parsing">
              <Spinner animation="border" role="status" />
              <strong>Parsing your resume…</strong>
              <span>This usually takes a few seconds.</span>
            </div>
          ) : (
            <>
              <div className="ssr-resume__drop-icon" aria-hidden>
                <CloudUpload size={28} strokeWidth={1.75} />
              </div>
              <strong>Drag and drop your resume here</strong>
              <span className="ssr-resume__drop-hint">PDF or DOCX • Max 10MB</span>
              <button
                type="button"
                className="ssr-resume__browse"
                onClick={(e) => {
                  e.stopPropagation();
                  openPicker();
                }}
              >
                Browse files
              </button>
            </>
          )}
        </div>

        {showSuccess ? (
          <>
            <div className="ssr-resume__file-row">
              <span className="ssr-resume__file-check" aria-hidden>
                <Check size={16} strokeWidth={2.5} />
              </span>
              <div className="ssr-resume__file-meta">
                <p className="ssr-resume__file-name">{resumeName}</p>
                <p className="ssr-resume__file-sub">{sizeMeta}</p>
              </div>
              <button
                type="button"
                className="ssr-resume__replace"
                onClick={openPicker}
                disabled={parsing}
              >
                Replace file
              </button>
            </div>

            <div className="ssr-resume__ready">
              <div className="ssr-resume__ready-icon" aria-hidden>
                <Sparkles size={18} />
              </div>
              <div className="ssr-resume__ready-copy">
                <strong>All set!</strong>
                <p>
                  Your profile draft is ready. Next, connect your LinkedIn to build a stronger
                  profile.
                </p>
              </div>
              <div className="ssr-resume__ready-linkedin" aria-hidden>
                <Linkedin size={18} />
              </div>
            </div>
          </>
        ) : null}

        <input
          ref={inputRef}
          id="fast-onboarding-resume"
          type="file"
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="d-none"
          aria-label="Upload resume PDF or DOCX"
          onChange={(e) => {
            takeFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />

        <footer className="ssr-resume__footer">
          <button type="button" className="ssr-resume__back" onClick={onBack} disabled={parsing}>
            <ArrowLeft size={16} aria-hidden />
            Back
          </button>
          <button
            type="button"
            className="ssr-resume__continue"
            onClick={onContinue}
            disabled={!showSuccess || parsing}
          >
            Continue to LinkedIn
            <ArrowRight size={16} aria-hidden />
          </button>
        </footer>
      </div>
    </div>
  );
}
